import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/ProjectsPage.scss';
import { 
    FaUser, FaTasks, FaCalendarAlt, FaFlagCheckered, FaEdit, FaEye, FaArchive, FaAngleRight,
    FaChevronDown, FaChevronRight, FaProjectDiagram, FaExclamation, FaGem, FaThList, 
    FaChartLine, FaPlus, FaSpinner, FaFolderOpen, FaTimes
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Constants for time calculations
 */
const DAYS_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const DEFAULT_PROJECT_DURATION_DAYS = 30;
const DEFAULT_GANTT_RANGE_DAYS = 90;

/**
 * Available status options for projects
 */
const PROJECT_STATUSES = ['Planning', 'Active', 'On Hold', 'Completed', 'Cancelled'];

/**
 * Available priority options for projects
 */
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

/**
 * Default department options (fallback if API fails)
 */
const DEFAULT_DEPARTMENTS = ['All'];

/**
 * Default colors for project visualization
 */
const PROJECT_COLORS = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#34495e', '#e67e22'];

/**
 * Format date as "Day Month Year" (e.g., "2 December 2025")
 * @param {string|Date} dateValue - Date to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
    });
};

/**
 * ProjectsPage Component
 * Main page for managing projects with Gantt chart visualization.
 * Allows creating, editing, and viewing projects with their associated tasks.
 */
const ProjectsPage = () => {
    const { apiFetch, user } = useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterDepartment, setFilterDepartment] = useState('All');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterProjectManager, setFilterProjectManager] = useState('');
    const [filterMyProjects, setFilterMyProjects] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'list', 'gantt', 'board'
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState(DEFAULT_DEPARTMENTS);
    const [expandedProjects, setExpandedProjects] = useState(new Set());
    const [expandedMilestones, setExpandedMilestones] = useState(new Set());
    const [expandedTasks, setExpandedTasks] = useState(new Set());
    const [projectTasks, setProjectTasks] = useState({});
    const [projectMilestones, setProjectMilestones] = useState({});

    // Form state for create/edit modal
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        department: '',
        assignedToUserId: '',
        startDate: '',
        expectedCompletionDate: '',
        status: 'Planning',
        priority: 'Medium',
        color: '#3498db',
        tags: ''
    });

    const userId = user?.userID || parseInt(localStorage.getItem('userID') || '0', 10);

    /**
     * Fetch all projects from the API
     */
    const fetchProjects = useCallback(async () => {
        setLoading(true);
        try {
            let url = '/api/Projects?';
            
            if (filterDepartment !== 'All') {
                url += `department=${encodeURIComponent(filterDepartment)}&`;
            }
            
            if (filterStatus) {
                url += `status=${encodeURIComponent(filterStatus)}&`;
            }

            if (filterProjectManager) {
                url += `assignedToUserId=${encodeURIComponent(filterProjectManager)}&`;
            }
            
            url += `includeInactive=${showCompleted}`;

            const response = await apiFetch(url);
            const data = await response.json();

            if (data.success) {
                let filteredProjects = data.data || [];
                
                // Client-side filter for "My Projects" (projects where user is assigned or has tasks)
                if (filterMyProjects && userId) {
                    filteredProjects = filteredProjects.filter(p => 
                        p.assignedToUserId === userId || 
                        p.createdByUserId === userId
                    );
                }
                
                setProjects(filteredProjects);
            } else {
                toast.error(data.message || 'Failed to load projects');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching projects:', error);
            toast.error('Failed to load projects');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, filterDepartment, filterStatus, filterProjectManager, filterMyProjects, showCompleted, userId]);

    /**
     * Fetch users for assignment dropdown
     */
    const fetchUsers = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/all');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setUsers(data.data);
            } else {
                setUsers([]);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching users for Projects page:', error);
            setUsers([]);
        }
    }, [apiFetch]);

    /**
     * Fetch departments from API
     */
    const fetchDepartments = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Department');
            const data = await response.json();
            if (data.success && data.data) {
                // Add 'All' option at the beginning for filtering
                const deptNames = data.data
                    .filter(d => d && d.isActive && d.departmentName)
                    .map(d => d.departmentName);
                setDepartments(['All', ...deptNames]);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching departments for Projects page:', error);
        }
    }, [apiFetch]);

    /**
     * Toggle project expansion in Gantt view and fetch tasks and milestones if needed
     */
    const toggleProjectExpanded = async (projectId) => {
        const newExpanded = new Set(expandedProjects);
        
        if (newExpanded.has(projectId)) {
            newExpanded.delete(projectId);
        } else {
            newExpanded.add(projectId);
            
            // Fetch tasks and milestones if not already loaded
            if (!projectTasks[projectId]) {
                try {
                    const response = await apiFetch(`/api/Projects/${projectId}`);
                    const data = await response.json();
                    
                    if (data.success && data.data.tasks) {
                        setProjectTasks(prev => ({
                            ...prev,
                            [projectId]: data.data.tasks
                        }));
                    }
                } catch (error) {
                    Sentry.captureException(error);
                    console.error('Error fetching project tasks:', error);
                }
            }
            
            // Fetch milestones if not already loaded
            if (!projectMilestones[projectId]) {
                try {
                    const response = await apiFetch(`/api/Projects/${projectId}/Milestones`);
                    const data = await response.json();
                    
                    if (data.success && data.data) {
                        setProjectMilestones(prev => ({
                            ...prev,
                            [projectId]: data.data
                        }));
                    }
                } catch (error) {
                    Sentry.captureException(error);
                    console.debug('Error fetching project milestones:', error);
                    setProjectMilestones(prev => ({
                        ...prev,
                        [projectId]: []
                    }));
                }
            }
        }
        
        setExpandedProjects(newExpanded);
    };

    /**
     * Toggle milestone expansion in Gantt view
     */
    const toggleMilestoneExpanded = (milestoneId) => {
        const newExpanded = new Set(expandedMilestones);
        if (newExpanded.has(milestoneId)) {
            newExpanded.delete(milestoneId);
        } else {
            newExpanded.add(milestoneId);
        }
        setExpandedMilestones(newExpanded);
    };

    /**
     * Toggle task expansion in Gantt view (for subtasks)
     */
    const toggleTaskExpanded = (taskId) => {
        const newExpanded = new Set(expandedTasks);
        if (newExpanded.has(taskId)) {
            newExpanded.delete(taskId);
        } else {
            newExpanded.add(taskId);
        }
        setExpandedTasks(newExpanded);
    };

    /**
     * Get subtasks for a given task
     */
    const getSubtasks = (tasks, parentId) => {
        if (!tasks) return [];
        return tasks.filter(t => t.parentActionItemId === parentId);
    };

    useEffect(() => {
        fetchProjects();
        fetchUsers();
        fetchDepartments();
    }, [fetchProjects, fetchUsers, fetchDepartments]);

    /**
     * Polling effect to automatically refresh projects data.
     * Polls every 30 seconds when the page is visible.
     * Pauses polling when the browser tab is not visible to save resources.
     */
    useEffect(() => {
        const POLLING_INTERVAL = 30000; // 30 seconds
        let pollInterval;
        
        /**
         * Silently fetches projects without showing loading state.
         * Used for background polling to update data.
         */
        const pollProjects = async () => {
            try {
                let url = '/api/Projects?';
                
                if (filterDepartment !== 'All') {
                    url += `department=${encodeURIComponent(filterDepartment)}&`;
                }
                
                if (filterStatus) {
                    url += `status=${encodeURIComponent(filterStatus)}&`;
                }

                if (filterProjectManager) {
                    url += `assignedToUserId=${encodeURIComponent(filterProjectManager)}&`;
                }
                
                url += `includeInactive=${showCompleted}`;

                const response = await apiFetch(url);
                const data = await response.json();

                if (data.success) {
                    let filteredProjects = data.data || [];
                    
                    if (filterMyProjects && userId) {
                        filteredProjects = filteredProjects.filter(p => 
                            p.assignedToUserId === userId || 
                            p.createdByUserId === userId
                        );
                    }
                    
                    setProjects(filteredProjects);
                }
            } catch (e) {
                Sentry.captureException(e);
                // Silently fail on polling errors to avoid spamming the user
                console.debug("Polling error:", e);
            }
        };
        
        /**
         * Handle visibility change - pause polling when tab is hidden.
         */
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden, clear the interval
                if (pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                }
            } else {
                // Page is visible again, restart polling
                if (!pollInterval) {
                    pollProjects(); // Fetch immediately when becoming visible
                    pollInterval = setInterval(pollProjects, POLLING_INTERVAL);
                }
            }
        };
        
        // Start polling
        pollInterval = setInterval(pollProjects, POLLING_INTERVAL);
        
        // Listen for visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Cleanup on unmount
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [apiFetch, filterDepartment, filterStatus, filterProjectManager, filterMyProjects, showCompleted, userId]);

    /**
     * Handle form input changes
     */
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /**
     * Reset form to default values
     */
    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            department: '',
            assignedToUserId: '',
            startDate: '',
            expectedCompletionDate: '',
            status: 'Planning',
            priority: 'Medium',
            color: '#3498db',
            tags: ''
        });
        setSelectedProject(null);
    };

    /**
     * Open create modal
     */
    const handleCreateNew = () => {
        resetForm();
        setShowCreateModal(true);
    };

    /**
     * Open edit modal with project data
     */
    const handleEdit = (project) => {
        setSelectedProject(project);
        setFormData({
            name: project.name || '',
            description: project.description || '',
            department: project.department || '',
            assignedToUserId: project.assignedToUserId?.toString() || '',
            startDate: project.startDate ? project.startDate.split('T')[0] : '',
            expectedCompletionDate: project.expectedCompletionDate ? project.expectedCompletionDate.split('T')[0] : '',
            status: project.status || 'Planning',
            priority: project.priority || 'Medium',
            color: project.color || '#3498db',
            tags: project.tags || ''
        });
        setShowCreateModal(true);
    };

    /**
     * Submit create/edit form
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Project name is required');
            return;
        }

        try {
            const payload = {
                name: formData.name,
                description: formData.description || null,
                department: formData.department || null,
                assignedToUserId: formData.assignedToUserId ? parseInt(formData.assignedToUserId) : null,
                startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
                expectedCompletionDate: formData.expectedCompletionDate ? new Date(formData.expectedCompletionDate).toISOString() : null,
                status: formData.status,
                priority: formData.priority,
                color: formData.color,
                tags: formData.tags || null,
                userId: userId
            };

            let response;
            if (selectedProject) {
                response = await apiFetch(`/api/Projects/${selectedProject.projectId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                response = await apiFetch('/api/Projects', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();

            if (data.success) {
                toast.success(selectedProject ? 'Project updated successfully' : 'Project created successfully');
                setShowCreateModal(false);
                resetForm();
                fetchProjects();
            } else {
                toast.error(data.message || 'Failed to save project');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving project:', error);
            toast.error('Failed to save project');
        }
    };

    /**
     * Archive a project
     */
    const handleArchive = async (projectId) => {
        if (!window.confirm('Are you sure you want to archive this project?')) return;

        try {
            const response = await apiFetch(`/api/Projects/${projectId}?userId=${userId}`, {
                method: 'DELETE'
            });
            const data = await response.json();

            if (data.success) {
                toast.success('Project archived successfully');
                fetchProjects();
            } else {
                toast.error(data.message || 'Failed to archive project');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error archiving project:', error);
            toast.error('Failed to archive project');
        }
    };

    /**
     * Get user name by ID
     */
    const getUserName = (userId) => {
        const foundUser = users.find(u => u.id === userId);
        return foundUser ? foundUser.name : 'Unassigned';
    };

    /**
     * Calculate date range for Gantt chart
     */
    const ganttDateRange = useMemo(() => {
        if (projects.length === 0) {
            const now = new Date();
            return {
                start: now,
                end: new Date(now.getTime() + DEFAULT_GANTT_RANGE_DAYS * DAYS_IN_MILLISECONDS)
            };
        }

        let minDate = new Date();
        let maxDate = new Date();

        projects.forEach(p => {
            if (p.startDate) {
                const start = new Date(p.startDate);
                if (start < minDate) minDate = start;
            }
            if (p.expectedCompletionDate) {
                const end = new Date(p.expectedCompletionDate);
                if (end > maxDate) maxDate = end;
            }
        });

        // Add buffer
        minDate.setDate(minDate.getDate() - 7);
        maxDate.setDate(maxDate.getDate() + 14);

        return { start: minDate, end: maxDate };
    }, [projects]);

    /**
     * Calculate position and width for Gantt bar
     */
    const calculateGanttBar = (project) => {
        const totalDays = (ganttDateRange.end - ganttDateRange.start) / DAYS_IN_MILLISECONDS;
        const startDate = project.startDate ? new Date(project.startDate) : new Date();
        const endDate = project.expectedCompletionDate ? new Date(project.expectedCompletionDate) : new Date(startDate.getTime() + DEFAULT_PROJECT_DURATION_DAYS * DAYS_IN_MILLISECONDS);

        const startOffset = (startDate - ganttDateRange.start) / DAYS_IN_MILLISECONDS;
        const duration = (endDate - startDate) / DAYS_IN_MILLISECONDS;

        const left = Math.max(0, (startOffset / totalDays) * 100);
        const width = Math.min(100 - left, (duration / totalDays) * 100);

        return { left: `${left}%`, width: `${Math.max(2, width)}%` };
    };

    /**
     * Calculate position and width for task Gantt bar
     */
    const calculateTaskGanttBar = (task) => {
        const totalDays = (ganttDateRange.end - ganttDateRange.start) / DAYS_IN_MILLISECONDS;
        const startDate = task.startDate ? new Date(task.startDate) : new Date();
        const endDate = task.expectedCompletionDate ? new Date(task.expectedCompletionDate) : 
                       (task.dueDate ? new Date(task.dueDate) : new Date(startDate.getTime() + 7 * DAYS_IN_MILLISECONDS));

        const startOffset = (startDate - ganttDateRange.start) / DAYS_IN_MILLISECONDS;
        const duration = (endDate - startDate) / DAYS_IN_MILLISECONDS;

        const left = Math.max(0, (startOffset / totalDays) * 100);
        const width = Math.min(100 - left, (Math.max(1, duration) / totalDays) * 100);

        return { left: `${left}%`, width: `${Math.max(1, width)}%` };
    };

    /**
     * Generate date labels for Gantt header
     */
    const ganttDateLabels = useMemo(() => {
        const labels = [];
        const totalDays = (ganttDateRange.end - ganttDateRange.start) / (24 * 60 * 60 * 1000);
        const interval = Math.max(7, Math.floor(totalDays / 10)); // Show ~10 labels

        for (let d = new Date(ganttDateRange.start); d <= ganttDateRange.end; d.setDate(d.getDate() + interval)) {
            labels.push(new Date(d));
        }

        return labels;
    }, [ganttDateRange]);

    /**
     * Render project card for list view
     */
    const renderProjectCard = (project) => (
        <div key={project.projectId} className={`project-card ${!project.isActive ? 'inactive' : ''}`}>
            <div className="project-card-header">
                <div className="project-color-bar" style={{ backgroundColor: project.color || '#3498db' }}></div>
                <div className="project-info">
                    <h3 className="project-name">{project.name}</h3>
                    <div className="project-meta">
                        <span className={`status-badge status-${project.status?.toLowerCase().replace(' ', '-')}`}>
                            {project.status}
                        </span>
                        <span className={`priority-badge priority-${project.priority?.toLowerCase()}`}>
                            {project.priority}
                        </span>
                        {project.department && <span className="department-badge">{project.department}</span>}
                    </div>
                </div>
            </div>

            <div className="project-card-body">
                {project.description && <p className="project-description">{project.description}</p>}
                
                <div className="project-details">
                    <div className="detail-item">
                        <FaUser />
                        <span>{getUserName(project.assignedToUserId)}</span>
                    </div>
                    <div className="detail-item">
                        <FaTasks />
                        <span>{project.taskCount || 0} tasks</span>
                    </div>
                    {project.startDate && (
                        <div className="detail-item">
                            <FaCalendarAlt />
                            <span>Start: {formatDate(project.startDate)}</span>
                        </div>
                    )}
                    {project.expectedCompletionDate && (
                        <div className="detail-item">
                            <FaFlagCheckered />
                            <span>Due: {formatDate(project.expectedCompletionDate)}</span>
                        </div>
                    )}
                </div>

                <div className="project-progress">
                    <div className="progress-bar">
                        <div 
                            className="progress-fill" 
                            style={{ width: `${project.progressPercent || 0}%` }}
                        ></div>
                    </div>
                    <span className="progress-text">{project.progressPercent || 0}%</span>
                </div>

                {project.tags && (
                    <div className="project-tags">
                        {project.tags.split(',').map((tag, idx) => (
                            <span key={idx} className="tag">{tag.trim()}</span>
                        ))}
                    </div>
                )}
            </div>

            <div className="project-card-actions">
                <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(project)}>
                    <FaEdit /> Edit
                </button>
                <button className="btn btn-sm btn-outline-info" onClick={() => navigate(`/projects/${project.projectId}`)}>
                    <FaEye /> View
                </button>
                {project.isActive && (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleArchive(project.projectId)}>
                        <FaArchive /> Archive
                    </button>
                )}
            </div>
        </div>
    );

    /**
     * Render a subtask row in Gantt
     */
    const renderSubtaskRow = (subtask) => (
        <div key={subtask.id} className="gantt-row gantt-subtask-row">
            <div className="gantt-project-info gantt-subtask-info">
                <span className="subtask-indent"></span>
                <span className="subtask-name">
                    <FaAngleRight /> {subtask.title || subtask.description || 'Subtask'}
                </span>
            </div>
            <div className="gantt-bar-container">
                <div 
                    className="gantt-bar gantt-bar-subtask"
                    style={{
                        ...calculateTaskGanttBar(subtask),
                        backgroundColor: subtask.status === 'Completed' || subtask.status === 'Closed' 
                            ? '#27ae60' 
                            : (subtask.status === 'In Progress' ? '#74b9ff' : '#b2bec3')
                    }}
                    title={`${subtask.title || 'Subtask'}: ${subtask.startDate ? formatDate(subtask.startDate) : 'No start'} - ${subtask.expectedCompletionDate || subtask.dueDate ? formatDate(subtask.expectedCompletionDate || subtask.dueDate) : 'No end'}`}
                >
                </div>
            </div>
        </div>
    );

    /**
     * Render a task row with subtasks in Gantt
     */
    const renderTaskRow = (task, allTasks, projectId) => {
        const subtasks = getSubtasks(allTasks, task.id);
        const hasSubtasks = subtasks.length > 0;
        const isTaskExpanded = expandedTasks.has(task.id);
        
        return (
            <React.Fragment key={task.id}>
                <div className="gantt-row gantt-task-row">
                    <div className="gantt-project-info gantt-task-info">
                        <span className="task-indent"></span>
                        {hasSubtasks ? (
                            <button 
                                className="gantt-expand-btn-sm"
                                onClick={() => toggleTaskExpanded(task.id)}
                                aria-label={isTaskExpanded ? 'Collapse' : 'Expand'}
                            >
                                {isTaskExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            </button>
                        ) : (
                            <span className="expand-placeholder"></span>
                        )}
                        <span className="task-name">
                            {task.title || task.description || 'Untitled Task'}
                        </span>
                        {hasSubtasks && (
                            <span className="subtask-count-badge">{subtasks.length}</span>
                        )}
                        <span className={`priority-indicator priority-${task.priority?.toLowerCase()}`}>
                            {task.priority}
                        </span>
                    </div>
                    <div className="gantt-bar-container">
                        <div 
                            className="gantt-bar gantt-bar-task"
                            style={{
                                ...calculateTaskGanttBar(task),
                                backgroundColor: task.status === 'Completed' || task.status === 'Closed' 
                                    ? '#2ecc71' 
                                    : (task.status === 'In Progress' ? '#3498db' : '#95a5a6')
                            }}
                            title={`${task.title || 'Task'}: ${task.startDate ? formatDate(task.startDate) : 'No start'} - ${task.expectedCompletionDate || task.dueDate ? formatDate(task.expectedCompletionDate || task.dueDate) : 'No end'}`}
                        >
                            <div 
                                className="gantt-progress"
                                style={{ width: `${task.progressPercent || 0}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
                
                {/* Subtasks (when expanded) */}
                {isTaskExpanded && subtasks.map(subtask => renderSubtaskRow(subtask, projectId))}
            </React.Fragment>
        );
    };

    /**
     * Render Gantt chart view with expandable hierarchy:
     * Project → Milestones → Tasks → Subtasks
     * Horizontally scrollable by dragging
     */
    const renderGanttView = () => (
        <div className="gantt-container">
            <div className="gantt-header">
                <div className="gantt-project-column">Project / Milestone / Task</div>
                <div className="gantt-timeline" style={{ overflowX: 'auto', cursor: 'grab' }}>
                    {ganttDateLabels.map((date, idx) => (
                        <div key={idx} className="gantt-date-label">
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                    ))}
                </div>
            </div>
            <div className="gantt-body" style={{ overflowY: 'auto', overflowX: 'auto', maxHeight: '70vh' }}>
                {projects.map(project => {
                    const isProjectExpanded = expandedProjects.has(project.projectId);
                    const tasks = projectTasks[project.projectId] || [];
                    const milestones = projectMilestones[project.projectId] || [];
                    const rootTasks = tasks.filter(t => !t.parentActionItemId);
                    
                    return (
                        <React.Fragment key={project.projectId}>
                            {/* Project Row */}
                            <div className="gantt-row gantt-project-row">
                                <div className="gantt-project-info">
                                    <button 
                                        className="gantt-expand-btn"
                                        onClick={() => toggleProjectExpanded(project.projectId)}
                                        aria-label={isProjectExpanded ? 'Collapse' : 'Expand'}
                                    >
                                        {isProjectExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                    </button>
                                    <FaProjectDiagram className="project-icon" />
                                    <span className="project-name" onClick={() => navigate(`/projects/${project.projectId}`)}>
                                        {project.name}
                                    </span>
                                    <span className="task-count-badge">{project.taskCount || 0} tasks</span>
                                    <span className={`status-indicator status-${project.status?.toLowerCase().replace(' ', '-')}`}></span>
                                </div>
                                <div className="gantt-bar-container">
                                    <div 
                                        className="gantt-bar gantt-bar-project"
                                        style={{
                                            ...calculateGanttBar(project),
                                            backgroundColor: project.color || '#3498db'
                                        }}
                                        title={`${project.name}: ${project.startDate ? formatDate(project.startDate) : 'No start'} - ${project.expectedCompletionDate ? formatDate(project.expectedCompletionDate) : 'No end'}`}
                                    >
                                        <div 
                                            className="gantt-progress"
                                            style={{ width: `${project.progressPercent || 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* When project is expanded, show milestones and tasks */}
                            {isProjectExpanded && (
                                <>
                                    {/* Milestone Rows */}
                                    {milestones.map(milestone => {
                                        const isMilestoneExpanded = expandedMilestones.has(milestone.milestoneId);
                                        // Find tasks that could be associated with this milestone
                                        // (Tasks without parent and within milestone date range)
                                        const milestoneTasks = rootTasks.filter(t => {
                                            if (!milestone.targetDate) return false;
                                            const taskEnd = t.expectedCompletionDate || t.dueDate ? new Date(t.expectedCompletionDate || t.dueDate) : null;
                                            const milestoneDate = new Date(milestone.targetDate);
                                            return taskEnd && taskEnd <= milestoneDate;
                                        });
                                        
                                        return (
                                            <React.Fragment key={milestone.milestoneId}>
                                                <div className="gantt-row gantt-milestone-row">
                                                    <div className="gantt-project-info gantt-milestone-info">
                                                        <span className="milestone-indent"></span>
                                                        <button 
                                                            className="gantt-expand-btn-sm"
                                                            onClick={() => toggleMilestoneExpanded(milestone.milestoneId)}
                                                            aria-label={isMilestoneExpanded ? 'Collapse' : 'Expand'}
                                                        >
                                                            {isMilestoneExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                                        </button>
                                                        <i className={`fas ${milestone.icon || 'fa-flag'} milestone-icon`} style={{ color: milestone.color || '#f39c12' }}></i>
                                                        <span className="milestone-name">{milestone.name}</span>
                                                        {milestone.isCritical && <span className="critical-badge"><FaExclamation /></span>}
                                                        <span className={`milestone-status-indicator status-${milestone.status?.toLowerCase().replace(' ', '-')}`}>
                                                            {milestone.status}
                                                        </span>
                                                    </div>
                                                    <div className="gantt-bar-container">
                                                        {milestone.targetDate && (
                                                            <div 
                                                                className={`gantt-bar gantt-bar-milestone ${milestone.status === 'Achieved' ? 'achieved' : ''}`}
                                                                style={{
                                                                    ...calculateMilestonePosition(milestone),
                                                                    backgroundColor: milestone.color || '#f39c12'
                                                                }}
                                                                title={`${milestone.name}: Target ${formatDate(milestone.targetDate)}`}
                                                            >
                                                                <FaGem />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Tasks under milestone (when expanded) */}
                                                {isMilestoneExpanded && milestoneTasks.map(task => 
                                                    renderTaskRow(task, tasks, project.projectId)
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    
                                    {/* Unassociated Tasks (not under any milestone) */}
                                    {rootTasks.filter(t => {
                                        // Tasks not associated with any milestone
                                        return !milestones.some(m => {
                                            if (!m.targetDate) return false;
                                            const taskEnd = t.expectedCompletionDate || t.dueDate ? new Date(t.expectedCompletionDate || t.dueDate) : null;
                                            const milestoneDate = new Date(m.targetDate);
                                            return taskEnd && taskEnd <= milestoneDate;
                                        });
                                    }).map(task => renderTaskRow(task, tasks, project.projectId))}
                                </>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );

    /**
     * Calculate milestone diamond position on Gantt chart
     */
    const calculateMilestonePosition = (milestone) => {
        if (!milestone.targetDate) return { left: '0%', width: '2%' };
        
        const totalDays = (ganttDateRange.end - ganttDateRange.start) / DAYS_IN_MILLISECONDS;
        const targetDate = new Date(milestone.targetDate);
        const startOffset = (targetDate - ganttDateRange.start) / DAYS_IN_MILLISECONDS;
        const left = Math.max(0, Math.min(98, (startOffset / totalDays) * 100));
        
        return { left: `${left}%`, width: '24px' };
    };

    return (
        <div className="projects-page">
<div className="page-header">
                <h1>
                    <FaProjectDiagram /> Project Management
                </h1>
                <p className="page-description">
                    Manage projects with Gantt chart visualization. Create tasks, track progress, and monitor deadlines.
                </p>
            </div>

            <div className="page-controls">
                <div className="filter-controls">
                    <div className="form-group">
                        <label>Department</label>
                        <select 
                            value={filterDepartment} 
                            onChange={(e) => setFilterDepartment(e.target.value)}
                            className="form-control"
                        >
                            {departments.map(dept => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Status</label>
                        <select 
                            value={filterStatus} 
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="form-control"
                        >
                            <option value="">All Statuses</option>
                            {PROJECT_STATUSES.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Project Manager</label>
                        <select 
                            value={filterProjectManager} 
                            onChange={(e) => setFilterProjectManager(e.target.value)}
                            className="form-control"
                        >
                            <option value="">All Managers</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-check">
                        <input 
                            type="checkbox" 
                            id="filterMyProjects"
                            checked={filterMyProjects}
                            onChange={(e) => setFilterMyProjects(e.target.checked)}
                        />
                        <label htmlFor="filterMyProjects">My Projects</label>
                    </div>

                    <div className="form-check">
                        <input 
                            type="checkbox" 
                            id="showCompleted"
                            checked={showCompleted}
                            onChange={(e) => setShowCompleted(e.target.checked)}
                        />
                        <label htmlFor="showCompleted">Show Archived</label>
                    </div>
                </div>

                <div className="view-controls">
                    <div className="view-toggle">
                        <button 
                            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title="List View"
                        >
                            <FaThList />
                            <span>List</span>
                        </button>
                        <button 
                            className={`view-btn ${viewMode === 'gantt' ? 'active' : ''}`}
                            onClick={() => setViewMode('gantt')}
                            title="Gantt Chart"
                        >
                            <FaChartLine />
                            <span>Gantt</span>
                        </button>
                    </div>

                    <button className="btn btn-primary" onClick={handleCreateNew}>
                        <FaPlus /> New Project
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <FaSpinner className="fa-spin fa-3x" />
                    <p>Loading projects...</p>
                </div>
            ) : projects.length === 0 ? (
                <div className="empty-state">
                    <FaFolderOpen className="fa-4x" />
                    <h3>No Projects Found</h3>
                    <p>Create your first project to get started with project management.</p>
                    <button className="btn btn-primary" onClick={handleCreateNew}>
                        <FaPlus /> Create Project
                    </button>
                </div>
            ) : viewMode === 'gantt' ? (
                renderGanttView()
            ) : (
                <div className="projects-grid">
                    {projects.map(project => renderProjectCard(project))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showCreateModal && (
                <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{selectedProject ? 'Edit Project' : 'Create New Project'}</h3>
                            <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="modal-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="name">Project Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="description">Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={3}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="form-row two-col">
                                <div className="form-group">
                                    <label htmlFor="department">Department</label>
                                    <select
                                        id="department"
                                        name="department"
                                        value={formData.department}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    >
                                        <option value="">Select Department</option>
                                        {departments.filter(d => d !== 'All').map(dept => (
                                            <option key={dept} value={dept}>{dept}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="assignedToUserId">Assigned To</label>
                                    <select
                                        id="assignedToUserId"
                                        name="assignedToUserId"
                                        value={formData.assignedToUserId}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    >
                                        <option value="">Unassigned</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row two-col">
                                <div className="form-group">
                                    <label htmlFor="startDate">Start Date</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="expectedCompletionDate">Expected Completion</label>
                                    <input
                                        type="date"
                                        id="expectedCompletionDate"
                                        name="expectedCompletionDate"
                                        value={formData.expectedCompletionDate}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="form-row three-col">
                                <div className="form-group">
                                    <label htmlFor="status">Status</label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    >
                                        {PROJECT_STATUSES.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="priority">Priority</label>
                                    <select
                                        id="priority"
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    >
                                        {PRIORITY_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="color">Color</label>
                                    <div className="color-picker">
                                        <input
                                            type="color"
                                            id="color"
                                            name="color"
                                            value={formData.color}
                                            onChange={handleInputChange}
                                        />
                                        <div className="color-presets">
                                            {PROJECT_COLORS.map((c, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    className={`color-preset ${formData.color === c ? 'selected' : ''}`}
                                                    style={{ backgroundColor: c }}
                                                    onClick={() => setFormData(prev => ({ ...prev, color: c }))}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="tags">Tags (comma-separated)</label>
                                    <input
                                        type="text"
                                        id="tags"
                                        name="tags"
                                        value={formData.tags}
                                        onChange={handleInputChange}
                                        placeholder="e.g., urgent, phase1, dev"
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {selectedProject ? 'Update Project' : 'Create Project'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectsPage;
